import UserStore from "./UserStore";
import { makeAutoObservable } from "mobx";
import { sortBy } from "lodash";
import { UserCompany } from "@/types/UserInfo";
import { DialogStore } from "@/dialogs/dialogDomain";
import { CompanyItem, failure, isOk, ok, pending, Result } from "@/common/common";
import { AccessType } from "@/types/enums/AccessType";
import { History } from "history";
import { AxiosInstance } from "axios";
import { OnboardingCandidateRoleType } from "@/types/enums/RoleType";
import ShareholdersStore from "./ShareholdersStore";
import { DropdownMenuItemProps } from "@/components-v2/Dropdown";
import {
  extractPortfolioContactsAdminUrl,
  PortfolioShareholdersResponse,
  SelectedPortfolioOption,
  toFollowUpCounts,
} from "@/portfolio/portfolioDomain";
import { TMessage } from "@/i18n/tt";
import { GetAdminsInCompaniesResponse, GetPortfolioCustomRoleRequest } from "@/types/Settings";
import { NamedOrganisationNumber } from "@/types/Transactions";
import { EditEmailForm, RevokeAllForm } from "@/portfolio/contacts/contactsDomain";
import { BooleanFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { CustomRole } from "@/common/customRole";
import { Language } from "@/i18n/languages";
import { formatDate } from "@/i18n/dateFormat";
import { ShareholderStatus } from "@/shareholderRegister/shareholderRegisterDomain";
import { ShareholderDTO } from "@/types/Shareholder";
import { FileUploadConfig } from "@/fileUpload/fileUploadDomain";
import { TMessageKey } from "@/i18n/types.gen";
import { TransactionType } from "@/types/enums/TransactionType";

export type LastTransaction = {
  date: string;
  transactionType: TransactionType;
  transactionLogId: number;
};

export type PortfolioCompany = {
  id?: string;
  companyId?: string;
  portfolioId?: string;
  portfolioName?: string;
  state?: CompanyState;
  starred: boolean;
  shareholderRegisterStatementStatus?: ShareholderRegisterStatementPortfolioStatus;
  beneficialOwnersPortfolioStatus?: BeneficialOwnersPortfolioStatus;
  shareCapital?: number;
  numberOfShareholders?: number;
  currency: string;
  ceoName?: string;
  chairPersonName?: string;
  isCEO?: boolean; // the below 4 isXXX flags are used for subsidiaries
  isChairperson?: boolean;
  isBoardmember?: boolean;
  isShareholder?: boolean;
  sourceOrgNoName?: string;
  ownershipInPercent?: number;
  role?: CustomRole;
  lastTransaction?: LastTransaction;
} & UserCompany;

export enum CompanyState {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
}

export type ShareholderRegisterStatementPortfolioStatus =
  | Approved
  | Rejected
  | Sent
  | NotSent
  | Overdue;

export const shareholderRegisterStatementPortfolioStatusTypes = [
  "Approved",
  "Rejected",
  "Sent",
  "NotSent",
  "Overdue",
] as const;

export type ShareholderRegisterStatementPortfolioStatusType =
  (typeof shareholderRegisterStatementPortfolioStatusTypes)[number];

export const shareholderRegisterStatementPortfolioStatusTypeLabels: Record<
  ShareholderRegisterStatementPortfolioStatusType,
  TMessageKey
> = {
  Approved: "approved",
  Rejected: "rejected",
  Sent: "submitted",
  NotSent: "not-submitted",
  Overdue: "overdue",
};

export type BeneficialOwnersPortfolioStatus = Sent | NotSent | Overdue;

export type Approved = { type: "Approved"; sentAt: string; sentBy: string };
export type Rejected = { type: "Rejected"; sentAt: string; sentBy: string };
export type Sent = { type: "Sent"; sentAt: string; sentBy: string };
export type NotSent = { type: "NotSent"; deadline?: string };
export type Overdue = { type: "Overdue"; deadline?: string };

export const initCompanyFilter = (
  cur: RadioFieldModel<CompanyItem | null> | undefined,
  companyItems: CompanyItem[],
  shareholders: ShareholderDTO[],
): RadioFieldModel<CompanyItem | null> => {
  const filteredCompanyItems = companyItems.filter((x) =>
    shareholders.some((s) => s.organizationNumber === x.organisationNumber),
  );
  const value =
    cur?.value &&
    filteredCompanyItems.some((x) => x.organisationNumber === cur.value?.organisationNumber)
      ? cur.value
      : null;
  return new RadioFieldModel<CompanyItem | null>({
    value,
    options: [null, ...filteredCompanyItems],
    idFn: (c) => (c ? c.organisationNumber : "all"),
    labelFn: (c) => (c ? { translated: c.companyName } : { key: "portfolio.all-companies" }),
  });
};

export default class PortfolioStore {
  readonly userStore: UserStore;
  readonly shareholdersStore: ShareholdersStore;
  readonly dialogStore: DialogStore;
  history: History;
  http: AxiosInstance;
  fetchPortfolioShareholdersResult?: Result<PortfolioShareholdersResponse>;
  fetchPortfolioAdminsResult?: Result<GetAdminsInCompaniesResponse, TMessage>;
  shareholderNameFilter = new StringFieldModel();
  adminNameFilter = new StringFieldModel();
  missingEmailModel = new BooleanFieldModel({ value: false });
  companyFilter?: RadioFieldModel<CompanyItem | null>;
  shareholderStatusFilter: ShareholderStatus = "current";
  companyInAdminsFilter?: RadioFieldModel<NamedOrganisationNumber | null>;
  importExcelConfig?: FileUploadConfig;

  fetchPortfolioCustomRoleResult?: Result<GetPortfolioCustomRoleRequest, TMessage>;

  constructor(
    userStore: UserStore,
    shareholdersStore: ShareholdersStore,
    dialogStore: DialogStore,
    history: History,
    http: AxiosInstance,
  ) {
    this.userStore = userStore;
    this.shareholdersStore = shareholdersStore;
    this.dialogStore = dialogStore;
    this.history = history;
    this.http = http;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setShareholderStatusFilter(s: ShareholderStatus) {
    this.shareholderStatusFilter = s;
  }

  get allCompanies(): PortfolioCompany[] {
    return sortBy(
      this.userStore.companies.map((c) => {
        return { ...c, starred: false };
      }),
      "name",
    );
  }

  *fetchPortfolioShareholders(portfolio: SelectedPortfolioOption) {
    this.fetchPortfolioShareholdersResult = pending();
    try {
      const response: PortfolioShareholdersResponse =
        yield this.dialogStore.apiService.getPortfolioShareholders(portfolio);
      this.fetchPortfolioShareholdersResult = ok(response);

      this.importExcelConfig = response.importExcelConfig;
      this.userStore.dispatchEvent([
        { type: "update-store", data: { followUpCounts: toFollowUpCounts(response) } },
      ]);
      this.initCompanyFilter(response.companyItems, response.shareholders);
    } catch (e) {
      this.fetchPortfolioShareholdersResult = failure();
    }
  }

  *fetchPortfolioAdmins(portfolio: SelectedPortfolioOption) {
    this.fetchPortfolioAdminsResult = pending();
    try {
      const response: GetAdminsInCompaniesResponse =
        yield this.dialogStore.apiService.getPortfolioAdmins(portfolio);
      this.fetchPortfolioAdminsResult = ok(response);
    } catch (e) {
      this.fetchPortfolioAdminsResult = failure({ key: "general-error" });
    }
  }

  *fetchPortfolioCustomRole(portfolio: SelectedPortfolioOption, portfolioId?: string) {
    this.fetchPortfolioCustomRoleResult = pending();
    try {
      const response: GetPortfolioCustomRoleRequest =
        yield this.dialogStore.apiService.getPortfolioCustomRole(portfolio, portfolioId);
      this.fetchPortfolioCustomRoleResult = ok(response);
    } catch (e) {
      this.fetchPortfolioCustomRoleResult = failure({ key: "general-error" });
    }
  }

  resetPortfolioAdmins() {
    this.fetchPortfolioAdminsResult = undefined;
  }

  resetPortfolioCustomRole() {
    this.fetchPortfolioCustomRoleResult = undefined;
  }

  initCompanyFilter(companyItems: CompanyItem[], shareholders: ShareholderDTO[]) {
    this.companyFilter = initCompanyFilter(this.companyFilter, companyItems, shareholders);
  }

  openEditShareholderDialog(
    locale: Language,
    shareholderId: number,
    orgNumber: string,
    portfolio: SelectedPortfolioOption,
  ) {
    this.dialogStore.openEditShareholderDialog({
      orgNumber,
      locale,
      shareholderId,
      onSuccess: () => {
        this.shareholdersStore.getShareholderDetails(shareholderId);
        this.fetchPortfolioShareholders(portfolio);
      },
      loadShareholders: () =>
        this.shareholdersStore.apiService.fetchShareholdersAsActors(orgNumber),
    });
  }

  initCompanyInAdminsFilter() {
    const companies = this.allCompanies.map((c) => ({ orgNo: c.organisationNumber, name: c.name }));
    const uniqueCompanies = new Map(companies.map((c) => [c.orgNo, c])).values();
    this.companyInAdminsFilter = new RadioFieldModel<NamedOrganisationNumber | null>({
      value: null,
      options: [null, ...uniqueCompanies],
      idFn: (c) => (c ? c.name : "all"),
      labelFn: (c) => (c ? { translated: c.name } : { key: "portfolio.all-companies" }),
    });
  }

  openEditEmailDialog({
    id,
    email,
    onSuccess,
    name,
    customRoleId,
  }: {
    id: string;
    email: string;
    onSuccess: (email: string) => void;
    name: string;
    customRoleId?: string;
  }) {
    this.dialogStore.openDialog({
      type: "editEmailAdmin",
      model: new EditEmailForm({
        apiService: this.userStore.apiService,
        dispatch: this.userStore.dispatchEvent,
        externalId: id,
        email,
        onSuccess,
        closeEditDialog: this.dialogStore.closeDialog,
        adminName: name,
        user: this.userStore.user,
        refreshUserInformation: this.userStore.refreshUserInformation,
        customRoleId,
      }),
    });
  }

  openRevokeAllDialog({
    orgNos,
    externalId,
    name,
    onSuccess,
    portfolio,
  }: {
    orgNos: string[];
    externalId: string;
    name: string;
    onSuccess: () => void;
    portfolio: SelectedPortfolioOption;
  }) {
    this.dialogStore.openDialog({
      type: "revokeAllAdminAccess",
      model: new RevokeAllForm({
        apiService: this.dialogStore.apiService,
        orgNos,
        externalId,
        name,
        closeRevokeAccessDialog: this.dialogStore.closeDialog,
        messageStore: this.dialogStore.messageStore,
        onNavigateBackToPortfolio: () =>
          this.userStore.dispatchEvent([
            {
              type: "navigate",
              url: extractPortfolioContactsAdminUrl(portfolio),
            },
          ]),
        state: "input",
        onSuccess,
      }),
    });
  }

  onRevokeAllAdminAccess(externalId: string) {
    if (isOk(this.fetchPortfolioAdminsResult)) {
      this.fetchPortfolioAdminsResult.data.admins =
        this.fetchPortfolioAdminsResult.data.admins.filter((i) => i.id !== externalId);
    }
  }
}

export type OnboardingCandidateResponse = {
  organisationNumber: string;
  companyName: string;
  namesWithRoles: NameWithRoles[];
  isCustomer: boolean;
  isShareholderPortalEnabled: boolean;
  isBoardPortalEnabled: boolean;
};

export type NameWithRoles = {
  name: string;
  roleTypes: OnboardingCandidateRoleType[];
};

export type InvitationResponse = {
  id: string;
  companyName: string;
  organisationNumber: string;
  state: string;
  currency: string;
  created: string;
};

export function concatenateShortcuts<T extends DropdownMenuItemProps>(
  standardShortcuts: T[],
  advancedShortcuts: T[],
  portfolioInstanceShortcuts: T[],
) {
  return [...standardShortcuts, ...advancedShortcuts, ...portfolioInstanceShortcuts];
}

export function getCompaniesWithAdminAccessType(companies: PortfolioCompany[]): PortfolioCompany[] {
  return companies
    .filter((f) => f.accessType === AccessType.ADMIN)
    .map((c) => {
      return { ...c, starred: false };
    });
}

export function formatARPortfolioDetails(
  language: Language,
  status: ShareholderRegisterStatementPortfolioStatus | undefined,
): TMessage {
  switch (status?.type) {
    case "NotSent":
      return { key: "ar-status.deadline", params: { date: formatDate(language, status.deadline) } };
    case "Overdue":
      return { key: "ar-status.deadline", params: { date: formatDate(language, status.deadline) } };
    case "Sent":
    case "Approved":
    case "Rejected":
      return {
        key: "ar-status.sent",
        params: {
          date: formatDate(language, status.sentAt),
          sender: status.sentBy,
        },
      };
    default:
      return { translated: "" };
  }
}

export function formatBeneficialOwnersPortfolioDetails(
  language: Language,
  status: BeneficialOwnersPortfolioStatus | undefined,
): TMessage {
  switch (status?.type) {
    case "NotSent":
      return { key: "beneficial-owners-portfolio-status.deadline" };
    case "Overdue":
      return { key: "beneficial-owners-portfolio-status.deadline" };
    case "Sent":
      return {
        key: "beneficial-owners-portfolio-status.sent",
        params: {
          date: formatDate(language, status.sentAt),
          sender: status.sentBy,
        },
      };
    default:
      return { translated: "" };
  }
}
